import React, { useContext, useState } from 'react';
import { StockLocationContext } from '../../../../../context/StockLocationContext';
import {
  DeleteStockLocationResponse,
  DeleteStockLocationVariables,
  StockLocationMutations,
} from '../../../../../graphql/stockLocation.graphql';
import useMutation from '../../../../../hooks/useMutation';
import { useToast } from '../../../../../providers/ToastProvider';
import { StockLocation, StockLocationInput } from '../../../../../types/stock_location';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { t } from '../../../../../types/translation/Translator';
import { Button, ButtonVariant } from '../../../../components/Button';
import { AlertModal } from '../../../../components/Modal';
import { TextInput } from '../../../../components/TextInput';
import { ToastType } from '../../../../components/Toast';

interface StockLocationDeleteModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  stockLocation: StockLocation;
}

export default function StockLocationDeleteModal({ open, setOpen, stockLocation }: StockLocationDeleteModalProps) {
  const { showToast } = useToast();

  const { stockLocations, setStockLocations } = useContext(StockLocationContext);

  const [stockLocationNameValidation, setStockLocationNameValidation] = useState('');

  const [remove, { loading }] = useMutation<DeleteStockLocationResponse, DeleteStockLocationVariables>(
    StockLocationMutations.remove,
    {
      variables: { stockLocations: [new StockLocationInput(stockLocation).forDelete()] },
      onCompleted: response => {
        const deletedStockLocation = response.deleteStockLocation[0];
        stockLocations.delete(deletedStockLocation.id);
        setStockLocations(new Map(stockLocations));
        showToast({
          type: ToastType.success,
          title: t(TranslationKey.stockLocationDeleteSuccessfullyToastTitle).singular.upper,
        });
        setOpen(false);
      },
    },
  );

  return (
    <AlertModal open={open} setOpen={setOpen} title={t(TranslationKey.deleteStockLocationModalTitle).singular.upper}>
      <div className='body-light grid gap-4'>
        <p>{t(TranslationKey.deleteStockLocationModalPrimaryText).singular.upper}</p>
        <ul className='list-inside list-disc'>
          <li>{t(TranslationKey.bin).plural.upper}</li>
          <li>{t(TranslationKey.product).plural.upper}</li>
          <li>{t(TranslationKey.task).plural.upper}</li>
          <li>{t(TranslationKey.order).plural.upper}</li>
          <li>{t(TranslationKey.role).plural.upper}</li>
        </ul>
        <p className='text-error-500'>
          {t(TranslationKey.deleteStockLocationModalActionCannotBeUndoneWarning).singular.upper}
        </p>
        <p>{t(TranslationKey.deleteStockLocationModalConfirmationText).singular.upper}</p>
        <TextInput
          placeholder={stockLocation.name}
          label={t(TranslationKey.deleteStockLocationModalValidateStockLocationNameInputLabel).singular.upper}
          onChange={setStockLocationNameValidation}
          value={stockLocationNameValidation}
        />
        <div className='flex items-center justify-end gap-2'>
          <Button
            text={t(TranslationKey.cancel).singular.upper}
            variant={ButtonVariant.link}
            onClick={() => setOpen(false)}
            disabled={loading}
          />
          <Button
            text={t(TranslationKey.deleteLocation).singular.upper}
            disabled={stockLocation.name !== stockLocationNameValidation}
            variant={ButtonVariant.danger}
            onClick={() => remove()}
            loading={loading}
          />
        </div>
      </div>
    </AlertModal>
  );
}
